import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { ReactElement } from 'react'
import { TerminalPane } from '../components/TerminalPane.tsx'
import { Button } from '../components/ui/index.ts'

/**
 * A single session's terminal, filling the whole window — the target of
 * "Pop out" from the terminal grid, so an operator can park one agent on a
 * second monitor.
 *
 * Deliberately has NO loader. The terminal needs only the session id,
 * which is already in the URL, and a route loader would fetch on the
 * server: an SSR request that 401s (for any reason — a cookie the API
 * origin isn't allowlisted for, an expired forward, a cold worker) bounces
 * the operator to /login instead of showing the terminal. Popping out a
 * terminal should never be able to log you out. Authentication happens
 * client-side when the pane mints its ticket, with the browser's own
 * cookie.
 */
export const Route = createFileRoute('/terminal/$sessionId')({
  component: TerminalWindow,
})

function TerminalWindow(): ReactElement {
  const { sessionId } = Route.useParams()
  const [generation, setGeneration] = useState(0)
  const [status, setStatus] = useState('connecting…')

  return (
    // `w-full`, not `w-screen`: this route renders inside the shell's
    // `<main>`, which already sits to the right of a 13rem sidebar, so
    // `100vw` was wider than its own column and put a horizontal scrollbar
    // on the whole page — on the one screen that is meant to be nothing
    // but terminal.
    <div className="relative h-screen w-full bg-canvas">
      <TerminalPane
        sessionId={sessionId}
        generation={generation}
        onStatusChange={setStatus}
      />
      {/* Floating rather than a header bar: the ask was for the popup to
          be *all* terminal, so the control overlays the corner instead of
          taking a row away from it. */}
      <div className="absolute top-1 right-2 flex items-center gap-2 rounded-md border border-subtle bg-elevated px-2 py-0.5 text-xs text-muted">
        <span>{status}</span>
        <Button
          variant="ghost"
          size="sm"
          title="Reconnect and resize to this window"
          onClick={() => {
            setGeneration((g) => g + 1)
          }}
        >
          Refresh
        </Button>
      </div>
    </div>
  )
}
